/**
 * Blocco B5.5 — Dialog per chiudere un giro con fermate non completate.
 *
 * Usage:
 *   <ChiudiGiroConRecuperiDialog
 *     open={dialogOpen}
 *     onClose={() => setDialogOpen(false)}
 *     mezzoData={mezzoData}      // contiene fermate + produttoriSaltati + produttoriRitirati
 *     doneIds={doneIds}           // Set<produttoreId> già ritirati nella sessione
 *     onConfirm={async ({ recuperi }) => { ... }}
 *   />
 */
import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { AlertTriangle } from "lucide-react";
import { MOTIVI_RECUPERO, MOTIVO_RECUPERO_LABEL } from "../../lib/constants";

export function ChiudiGiroConRecuperiDialog({
  open, onClose, mezzoData, doneIds, onConfirm,
}) {
  const [recuperiMap, setRecuperiMap] = useState({}); // { pid: { motivo, note } }
  const [submitting, setSubmitting] = useState(false);

  // Calcola fermate "residue": non ritirate.
  // Include SIA le fermate attive non completate SIA le fermate saltate.
  // Nel payload GET /api/giro/operativo-zone (e /api/giro/today), le saltate
  // sono in `fermateSaltate` separatamente da `fermate`: dobbiamo unire i due
  // insiemi per non "dimenticare" recuperi (regola: skip = visita non
  // completata → deve diventare recupero).
  const residue = useMemo(() => {
    if (!mezzoData) return [];
    const saltatiSet = new Set(mezzoData.produttoriSaltati || []);
    const ritirati = new Set([...(doneIds || []), ...(mezzoData.produttoriRitirati || [])]);
    const attive = (mezzoData.fermate || []).filter((s) => !ritirati.has(s.id));
    const saltateNonRitirate = (mezzoData.fermateSaltate || []).filter((s) => !ritirati.has(s.id));
    // Deduplica per id nel caso raro in cui una stessa fermata compaia in entrambe le liste
    const seen = new Set();
    const merged = [];
    for (const s of [...attive, ...saltateNonRitirate]) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      merged.push({ ...s, _saltata: saltatiSet.has(s.id) || s.saltato === true });
    }
    return merged;
  }, [mezzoData, doneIds]);

  const setMotivo = (pid, motivo) =>
    setRecuperiMap((m) => ({ ...m, [pid]: { ...(m[pid] || {}), motivo } }));
  const setNote = (pid, note) =>
    setRecuperiMap((m) => ({ ...m, [pid]: { ...(m[pid] || {}), note } }));

  const allHaveMotivo = residue.every((s) => recuperiMap[s.id]?.motivo);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const recuperi = residue.map((s) => ({
        produttoreId: s.id,
        motivo: recuperiMap[s.id]?.motivo || "ALTRO",
        note: recuperiMap[s.id]?.note || "",
      }));
      await onConfirm({ recuperi });
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="dialog-chiudi-giro-recuperi">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {residue.length > 0 && <AlertTriangle className="text-[#F57C00]" size={20} />}
            {residue.length > 0
              ? `Ci sono ${residue.length} fermate non completate`
              : "Chiudi giro"}
          </DialogTitle>
        </DialogHeader>

        {residue.length > 0 && (
          <p className="text-sm text-slate-600 mb-1">
            Vuoi chiudere il giro e marcarle come <strong>da recuperare</strong>?
            Indica il motivo per ciascuna.
          </p>
        )}

        {/* Avviso di chiusura DEFINITIVA */}
        <div
          className="border-2 border-[#C62828] bg-[#C62828]/5 rounded-lg p-3"
          data-testid="avviso-chiusura-definitiva"
        >
          <div className="flex items-center gap-2 font-semibold text-sm text-[#C62828]">
            <AlertTriangle size={16} />
            Attenzione: stai per chiudere definitivamente il giro
          </div>
          <p className="text-xs text-slate-700 mt-1">
            Una volta chiuso il giro non sarà più possibile registrare, modificare o
            annullare ritiri, né saltare o ripristinare fermate. Le fermate non
            completate verranno trasformate in recuperi. Controlla prima di
            proseguire che ritiri, quantità e note siano corretti.
          </p>
        </div>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
          {residue.map((s) => (
            <div key={s.id} className={`border rounded-lg p-3 ${s._saltata ? "border-[#F57C00]/40 bg-[#F57C00]/5" : "border-slate-200 bg-slate-50"}`} data-testid={`recupero-row-${s.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="font-semibold text-sm">{s.ragioneSociale}</div>
                {s._saltata && (
                  <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-[#F57C00] text-white" data-testid={`recupero-badge-saltata-${s.id}`}>
                    Saltata
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mb-2">
                {s.indirizzo}{s.cap ? `, ${s.cap}` : ""}{s.citta ? ` ${s.citta}` : ""}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label htmlFor={`motivo-${s.id}`} className="text-xs">Motivo *</Label>
                  <Select value={recuperiMap[s.id]?.motivo || ""} onValueChange={(v) => setMotivo(s.id, v)}>
                    <SelectTrigger id={`motivo-${s.id}`} className="h-9" data-testid={`recupero-motivo-${s.id}`}>
                      <SelectValue placeholder="Seleziona motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {MOTIVI_RECUPERO.map((m) => (
                        <SelectItem key={m} value={m}>{MOTIVO_RECUPERO_LABEL[m]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor={`note-${s.id}`} className="text-xs">Note</Label>
                  <Textarea
                    id={`note-${s.id}`}
                    rows={1}
                    value={recuperiMap[s.id]?.note || ""}
                    onChange={(e) => setNote(s.id, e.target.value)}
                    placeholder="(facoltativo)"
                    data-testid={`recupero-note-${s.id}`}
                  />
                </div>
              </div>
            </div>
          ))}
          {residue.length === 0 && (
            <p className="text-sm text-slate-500 italic">
              Tutte le fermate previste sono state completate. Conferma per chiudere il giro.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="recupero-annulla">
            Torna al giro
          </Button>
          <Button
            onClick={confirm}
            disabled={submitting || (residue.length > 0 && !allHaveMotivo)}
            className="bg-[#C62828] hover:bg-[#A31F1F] text-white"
            data-testid="recupero-conferma"
          >
            {residue.length > 0
              ? "Chiudi definitivamente e crea i recuperi"
              : "Chiudi definitivamente il giro"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
